import { Worksheet } from '../../types/worksheet';
import { useUpdateWorksheets } from '../../queries/use-update-worksheets';
import { useMemo, useEffect, Fragment, useRef } from 'react';
import { FormProvider, useForm, useWatch, useFieldArray } from 'react-hook-form';
import { toastNotification } from '../../components/toast/Toast';
import { isNullOrUndefined } from '../new-workpaper/utils';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import LinkPriorWorksheetRow from './LinkPriorWorksheetRow';
import LinkPriorWorksheetsResult from './LinkPriorWorksheetsResult';
import LinkingBanner from './LinkingBanner';
import { useInView, AnimatePresence, motion } from 'framer-motion';
import { removeSuccessfulPriorWsLinkings } from './utils';

// Estimated height of each LinkPriorWorksheetRow
const ITEM_HEIGHT = 56;

/**
 * Converts array of worksheets into a Record/map for quick lookups
 * @param worksheets
 * @returns Record object with worksheet IDs as keys and Worksheet objects as values
 */
function toMap(worksheets: Worksheet[]): Record<string, Worksheet> {
    return worksheets.reduce((wsMap, ws) => {
        wsMap[ws.id] = ws;
        return wsMap;
    }, {} as Record<string, Worksheet>);
}

export type WorksheetLinking = {
    currentWorksheet: { id: string; name: string };
    priorWorksheet?: { id: string; name: string }; // represents user's current selection. Can be modified before confirmation.
    index: number;
};

export type WorksheetLinkingFormData = {
    worksheetLinkings: WorksheetLinking[];
};

type LinkPriorWorksheetsFormProps = {
    priorWorkpaperId: string;
    selectedWorksheets: Worksheet[];
    currentWorksheets: Worksheet[];
    priorWorksheets: Worksheet[];
    isLoading?: boolean;
    onLink: (data: { worksheetsLinked: number }) => void;
};

function LinkPriorWorksheetsForm({
    priorWorkpaperId,
    selectedWorksheets,
    currentWorksheets,
    priorWorksheets,
    isLoading = false,
    onLink,
}: LinkPriorWorksheetsFormProps): JSX.Element {
    const updateWorksheetsQuery = useUpdateWorksheets();
    const bannerRef = useRef<HTMLDivElement>(null);
    const bannerInView = useInView(bannerRef);
    const priorWorksheetsMap = useMemo(() => toMap(priorWorksheets), [priorWorksheets]);

    // Initialise the worksheetLinkings form
    // Stores current & prior worksheet details for each selected ws, and its index in original selectedWorksheets array
    const form = useForm<WorksheetLinkingFormData>({
        defaultValues: {
            worksheetLinkings: selectedWorksheets.map((ws, index) => ({
                currentWorksheet: { id: ws.id, name: ws.name },
                priorWorksheet: !isNullOrUndefined(ws.priorWorksheetId)
                    ? { id: priorWorksheetsMap[ws.priorWorksheetId].id, name: priorWorksheetsMap[ws.priorWorksheetId].name }
                    : undefined,
                index,
            })),
        },
    });

    const { control, handleSubmit, getValues } = form;
    const { fields, remove } = useFieldArray({
        name: 'worksheetLinkings',
        control,
    });

    function onSubmit(data: WorksheetLinkingFormData): void {
        const dirtyWorksheets = data.worksheetLinkings
            .filter((worksheetLinking, idx) => {
                const dirtyField = form.formState.dirtyFields.worksheetLinkings?.at(idx);
                return Boolean(dirtyField);
            })
            .map((dw) => {
                return {
                    id: dw.currentWorksheet.id,
                    fields: {
                        priorWorksheetId: dw.priorWorksheet?.id,
                    },
                };
            });
        updateWorksheetsQuery.mutate(dirtyWorksheets, {
            onSuccess: (res) => {
                const linkingResults = res.results.reduce(
                    (acc, lr) => {
                        if (lr.error === undefined && lr.data !== undefined) acc.successful++;
                        else if (lr.error !== undefined && lr.data === undefined) acc.failed++;
                        return acc;
                    },
                    { successful: 0, failed: 0 }
                );

                onLink({ worksheetsLinked: linkingResults.successful });
                toastNotification(
                    'success',
                    `${linkingResults.successful} worksheets succesfully linked. ${linkingResults.failed} failed.`
                );
            },
            onError: (err) => {
                // if any worksheetLinkings fail, it will error. Below removes the successfully linked worksheets from the form.
                removeSuccessfulPriorWsLinkings(err, getValues, remove, 'worksheetLinkings');
            },
        });
    }

    const worksheetLinkings = useWatch({ control, name: 'worksheetLinkings' });

    // List of all worksheets in the form that have already had a prior worksheet selected. Used to disable selected options.
    const usedWorksheetLinkings = useMemo(
        () =>
            new Set([
                ...worksheetLinkings.filter((v) => v.priorWorksheet).map((v) => v.priorWorksheet!.id),
                ...currentWorksheets.filter((cw) => cw.priorWorksheetId).map((cw) => cw.priorWorksheetId!),
            ]),
        [worksheetLinkings, currentWorksheets]
    );

    return (
        <FormProvider {...form}>
            <form
                onSubmit={(e): void => {
                    e.stopPropagation();
                    handleSubmit(onSubmit)(e);
                }}
                className="flex flex-col gap-2"
            >
                {updateWorksheetsQuery.error?.response?.data && (
                    <LinkPriorWorksheetsResult status="error" results={updateWorksheetsQuery.error.response.data} />
                )}
                {updateWorksheetsQuery.data && <LinkPriorWorksheetsResult status="success" results={updateWorksheetsQuery.data} />}

                <div ref={bannerRef}>
                    <LinkingBanner
                        priorWorkpaperId={priorWorkpaperId}
                        accountWorksheets={selectedWorksheets.some((ws) => !isNullOrUndefined(ws.account))}
                        isLoading={isLoading}
                        isSaving={updateWorksheetsQuery.isLoading}
                        bannerRef={bannerRef}
                    />
                </div>
                <div className="relative h-fit ">
                    <div className="grid grid-cols-[400px_100px_auto] gap-x-8 border rounded-md">
                        <div className="grid grid-cols-[subgrid] col-span-full border-b py-2 text-sm font-medium text-gray-800 bg-gray-100 rounded-t px-4 w-full">
                            <p className="col-span-2">Current Workpaper</p>
                            <p>Prior Workpaper</p>
                        </div>
                        {isLoading && <LoadingIndicator />}
                        {!isLoading && (
                            <div className="grid grid-cols-[subgrid] col-span-full h-[450px]">
                                {/* Virtualized list to handle a large amount of worksheets */}
                                <AutoSizer>
                                    {/* @ts-ignore */}
                                    {({ height, width }): JSX.Element => {
                                        return (
                                            <FixedSizeList
                                                height={height}
                                                width={width}
                                                itemCount={fields.length}
                                                itemSize={ITEM_HEIGHT}
                                                itemData={fields}
                                                itemKey={(index, data) => data[index].id}
                                            >
                                                {({ data, index, style }): JSX.Element => {
                                                    const item = data[index];
                                                    return (
                                                        <LinkPriorWorksheetRow
                                                            worksheetLinking={item}
                                                            priorWorksheets={priorWorksheets}
                                                            idx={index}
                                                            usedWorksheetLinkings={usedWorksheetLinkings}
                                                            style={style}
                                                            key={item.id}
                                                        />
                                                    );
                                                }}
                                            </FixedSizeList>
                                        );
                                    }}
                                </AutoSizer>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

function LoadingIndicator(): JSX.Element {
    return (
        <>
            {[...Array(50).keys()]
                .map((i) => i + 1)
                .map((i) => {
                    return (
                        <Fragment key={i}>
                            <div className="col-span-2 py-2 px-4 h-[50px] w-full">
                                <div className="bg-gray-200 animate-pulse h-full w-full rounded"></div>
                            </div>
                            <div className=" h-[50px] py-2 px-4 w-full">
                                <div className="bg-gray-200 animate-pulse  h-full w-full rounded"></div>
                            </div>
                        </Fragment>
                    );
                })}
        </>
    );
}

export default LinkPriorWorksheetsForm;
